import { useCallback, useRef } from 'react'
import { apiPost, parseApiResponse } from '../../../../../utils/api'
import { formatApiError } from '../../../../../utils/errorMessages'

interface UseDistanceCalculationsParams {
  readonly onDistanceCalculated?: (distance: number) => void
  readonly onDistanceCalculatingChange?: (isCalculating: boolean) => void
  readonly onError?: (title: string, message: string) => void
}

export const useDistanceCalculations = ({
  onDistanceCalculated,
  onDistanceCalculatingChange,
  onError,
}: UseDistanceCalculationsParams) => {
  const distanceCalculationTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const lastCalculatedDistanceRef = useRef<number>(0)
  const pendingDistanceCalculationsRef = useRef<number>(0)

  const handleApiError = useCallback(
    (error: unknown) => {
      const { title, message } = formatApiError(error)
      onError?.(title, message)
      onDistanceCalculated?.(0)
    },
    [onError, onDistanceCalculated]
  )

  const performCalculation = useCallback(
    async (polygon: { coordinates: number[][][] }) => {
      pendingDistanceCalculationsRef.current += 1
      try {
        const response = await apiPost('/zones/calculate-distance', {
          polygon: { type: 'Polygon', coordinates: polygon.coordinates },
        })

        if (!response.ok) {
          const errorText = await response.text()
          let errorMessage = 'Failed to calculate distance from coast'
          try {
            const errorData = JSON.parse(errorText)
            errorMessage = errorData.message ?? errorMessage
          } catch {
            errorMessage = `Server error: ${response.status} ${response.statusText}`
          }
          throw new Error(errorMessage)
        }

        const result = await parseApiResponse<{ distance: number }>(response)
        if (result.success && result.data) {
          lastCalculatedDistanceRef.current = result.data.distance
          onDistanceCalculated?.(result.data.distance / 1000)
        } else {
          throw new Error(
            result.message ?? 'Failed to calculate distance from coast'
          )
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Distance calculation error:', error)
        handleApiError(error)
      } finally {
        pendingDistanceCalculationsRef.current = Math.max(
          0,
          pendingDistanceCalculationsRef.current - 1
        )
        if (pendingDistanceCalculationsRef.current === 0) {
          onDistanceCalculatingChange?.(false)
        }
      }
    },
    [handleApiError, onDistanceCalculated, onDistanceCalculatingChange]
  )

  const calculateDistanceFromCoast = useCallback(
    async (polygon: { type: string; coordinates: number[][][] } | null) => {
      if (!polygon) {
        lastCalculatedDistanceRef.current = 0
        pendingDistanceCalculationsRef.current = 0
        onDistanceCalculated?.(0)
        onDistanceCalculatingChange?.(false)
        return
      }

      if (distanceCalculationTimeoutRef.current) {
        clearTimeout(distanceCalculationTimeoutRef.current)
      }

      onDistanceCalculatingChange?.(true)
      distanceCalculationTimeoutRef.current = setTimeout(() => {
        performCalculation(polygon)
      }, 500)
    },
    [onDistanceCalculated, onDistanceCalculatingChange, performCalculation]
  )

  return {
    calculateDistanceFromCoast,
    lastCalculatedDistanceRef,
  }
}
